import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import Layout from '../../components/Layout';

function UsersListGrouped() {
  const [groupedUsers, setGroupedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [expandedDepts, setExpandedDepts] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [allHotels, setAllHotels] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    passwordConfirmation: '',
    selectedHotels: [],
    dept_id: ''
  });

  // Fetch all hotels and departments for edit form
  useEffect(() => {
    // Get all hotels
    axios.get('/list/hotels')
      .then(res => {
        const parsedHotels = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        setAllHotels(Array.isArray(parsedHotels) ? parsedHotels : []);
      })
      .catch(err => console.error('Error fetching all hotels:', err));
    
    // Get all departments
    axios.get('/list/departments')
      .then(res => {
        const parsedDepts = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        setAllDepartments(Array.isArray(parsedDepts) ? parsedDepts : []);
      })
      .catch(err => console.error('Error fetching departments:', err));
  }, []);

  // Fetch users grouped by hotel and department (no filter - show all)
  useEffect(() => {
    const fetchUsers = () => {
      setLoading(true);
      
      axios({
        method: 'POST',
        url: '/list/users',
        data: {},
        headers: {
          'Content-Type': 'application/json'
        }
      })
        .then(res => {
          if (res.data && res.data.status === 200) {
            const data = res.data.data;
            setGroupedUsers(data.hotels || []);
            
            // Auto-expand all departments
            const deptsExpanded = {};
            data.hotels?.forEach(hotel => {
              hotel.departments?.forEach(dept => {
                deptsExpanded[`${hotel.hotel_id}-${dept.dept_id}`] = true;
              });
            });
            setExpandedDepts(deptsExpanded);
          }
          setLoading(false);
        })
        .catch(err => {

          setGroupedUsers([]);
          setLoading(false);
        });
    };
    
    fetchUsers();
  }, []); // Run once on mount

  const toggleDept = (hotelId, deptId) => {
    const key = `${hotelId}-${deptId}`;
    setExpandedDepts(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getAvatarUrl = (imgPath) => {
    if (!imgPath) return null;
    if (imgPath.startsWith('http')) return imgPath;
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8000';
    return `${baseUrl}${imgPath}`;
  };

  const handleUpdateUser = async () => {
    try {
      // If password is being changed, validate confirmation
      if (formData.password && formData.password.trim() !== '') {
        if (formData.passwordConfirmation !== 'dianfriansyah') {
          alert('Invalid password confirmation. Please enter the correct confirmation code.');
          return;
        }
      }
      
      const response = await axios.post('/update/user', {
        user_id: selectedUser.id,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password, // Will be empty if not changing
        dept_id: formData.dept_id,
        hotel_ids: formData.selectedHotels
      });
      
      if (response.data.status === 200) {
        alert('User updated successfully!');
        setShowModal(false);
        setEditMode(false);
        // Refresh users list
        window.location.reload();
      }
    } catch (error) {

      alert('Error updating user: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteUser = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedUser.name}?\n\nUser ID: ${selectedUser.id}\nEmail: ${selectedUser.email}\n\nThis action cannot be undone!`)) {
      return;
    }
    
    try {
      const response = await axios({
        method: 'DELETE',
        url: `/delete/user/${selectedUser.id}`,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.status === 200) {
        alert(`User "${selectedUser.name}" has been deleted successfully!`);
        setShowModal(false);
        setSelectedUser(null);
        window.location.href = window.location.href.split('?')[0] + '?t=' + new Date().getTime();
      } else {
        alert('Failed to delete user. Please try again.');
      }
    } catch (error) {

      alert('Error deleting user: ' + (error.response?.data?.message || error.message));
    }
  };

  const toggleHotelSelection = (hotelId) => {
    setFormData(prev => ({
      ...prev,
      selectedHotels: prev.selectedHotels.includes(hotelId.toString())
        ? prev.selectedHotels.filter(id => id !== hotelId.toString())
        : [...prev.selectedHotels, hotelId.toString()]
    }));
  };

  const getRoleBadge = (roleName) => {
    const colors = {
      'Super Admin': 'bg-red-100 text-red-700',
      'Admin': 'bg-blue-100 text-blue-700',
      'Manager': 'bg-purple-100 text-purple-700',
      'User': 'bg-green-100 text-green-700',
      'Staff': 'bg-gray-100 text-gray-700'
    };
    
    const colorClass = colors[roleName] || 'bg-gray-100 text-gray-700';
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
        {roleName}
      </span>
    );
  };

  // Filter users based on search text
  const filteredUsers = groupedUsers.map(hotel => {
    if (!searchText) return hotel;
    
    const search = searchText.toLowerCase();
    
    // If hotel name matches, return all departments and users
    if (hotel.hotel_name?.toLowerCase().includes(search)) {
      return hotel;
    }
    
    // Filter departments and users within this hotel
    const filteredDepartments = hotel.departments?.map(dept => {
      // If department name matches, return all users in this department
      if (dept.dept_name?.toLowerCase().includes(search)) {
        return dept;
      }
      
      // Filter users within this department
      const filteredDeptUsers = dept.users?.filter(user => {
        const nameMatch = user.name?.toLowerCase().includes(search);
        const emailMatch = user.email?.toLowerCase().includes(search);
        const phoneMatch = user.phone?.toLowerCase().includes(search);
        return nameMatch || emailMatch || phoneMatch;
      });
      
      // Only return department if it has matching users
      if (filteredDeptUsers && filteredDeptUsers.length > 0) {
        return {
          ...dept,
          users: filteredDeptUsers
        };
      }
      
      return null;
    }).filter(dept => dept !== null);
    
    // Only return hotel if it has matching departments
    if (filteredDepartments && filteredDepartments.length > 0) {
      return {
        ...hotel,
        departments: filteredDepartments
      };
    }
    
    return null;
  }).filter(hotel => hotel !== null);

  return (
    <Layout title="Users Management" subtitle="Users organized by hotel and department">
      <style>{`
        .tab-scroll-container {
          -webkit-overflow-scrolling: touch;
        }
        .tab-scroll-container::-webkit-scrollbar {
          height: 8px;
        }
        .tab-scroll-container::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        .tab-scroll-container::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }
        .tab-scroll-container::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
      <div className="p-4 sm:p-6 md:p-8">
        
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
              Users Management
            </h1>
            <div className="text-sm text-gray-600">
              <span className="font-semibold">{filteredUsers.length}</span> hotels · 
              <span className="font-semibold ml-1">
                {filteredUsers.reduce((sum, hotel) => sum + hotel.departments.reduce((dSum, dept) => dSum + dept.users.length, 0), 0)}
              </span> users
            </div>
          </div>
          
          {/* Search */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search by name, email, phone, hotel, or department..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              />
              <svg className="absolute left-3 top-3 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchText && (
                <div className="absolute right-3 top-3">
                  <span className="text-xs text-gray-500">
                    {filteredUsers.reduce((sum, hotel) => 
                      sum + hotel.departments.reduce((dSum, dept) => dSum + dept.users.length, 0), 0
                    )} results
                  </span>
                </div>
              )}
            </div>
            
            {searchText && (
              <button
                onClick={() => setSearchText('')}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Clear Search
              </button>
            )}
          </div>
        </div>

        {/* Loading */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <p className="text-gray-500 text-lg">No users found</p>
            {searchText && <p className="text-gray-400 text-sm mt-2">Try adjusting your search</p>}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Hotel Tabs */}
            <div className="border-b border-gray-200 bg-gray-50 relative">
              <div className="flex overflow-x-auto tab-scroll-container">
                {filteredUsers.map((hotel, index) => {

                  return (
                    <button
                      key={hotel.hotel_id}
                      onClick={() => {

                        setActiveTab(index);
                      }}
                      className={`flex-shrink-0 px-6 py-4 font-semibold text-sm whitespace-nowrap transition-all border-b-2 ${
                        activeTab === index
                          ? 'text-purple-600 border-purple-600 bg-white'
                          : 'text-gray-600 border-transparent hover:text-gray-800 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🏨</span>
                        <div className="text-left">
                          <div className="font-bold">{hotel.hotel_name}</div>
                          <div className="text-xs text-gray-500">
                            {hotel.departments.reduce((sum, dept) => sum + dept.users.length, 0)} users
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {filteredUsers.length > 4 && (
                <div className="text-center py-1 text-xs text-gray-500 bg-gray-100">
                  ← Scroll to see all {filteredUsers.length} hotels →
                </div>
              )}
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {filteredUsers[activeTab] && (
                <div className="space-y-4">
                  {/* Departments */}
                  {filteredUsers[activeTab].departments.map((dept) => (
                      <div key={dept.dept_id} className="border border-gray-200 rounded-lg overflow-hidden">
                        {/* Department Header */}
                        <div 
                          className="bg-gray-50 p-3 cursor-pointer flex items-center justify-between hover:bg-gray-100 transition-colors"
                          onClick={() => toggleDept(filteredUsers[activeTab].hotel_id, dept.dept_id)}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">🏢</span>
                            <div>
                              <h3 className="font-semibold text-gray-800">{dept.dept_name}</h3>
                              <p className="text-xs text-gray-500">Code: {dept.dept_code}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs font-medium">
                              {dept.users.length} users
                            </span>
                            <svg 
                              className={`w-5 h-5 text-gray-600 transition-transform ${expandedDepts[`${dept.hotel_id}-${dept.dept_id}`] ? 'rotate-180' : ''}`}
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>

                        {/* Users Table */}
                        {expandedDepts[`${filteredUsers[activeTab].hotel_id}-${dept.dept_id}`] && (
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">User</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Email</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Phone</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Role</th>
                                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {dept.users.map((user) => (
                                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3">
                                      <div className="flex items-center gap-3">
                                        {getAvatarUrl(user.img_user) ? (
                                          <img 
                                            src={getAvatarUrl(user.img_user)} 
                                            alt={user.name}
                                            className="w-10 h-10 rounded-full object-cover border-2 border-purple-200 bg-gray-200"
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                              e.target.nextSibling.style.display = 'flex';
                                            }}
                                          />
                                        ) : null}
                                        <div 
                                          className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-semibold text-sm"
                                          style={{ display: getAvatarUrl(user.img_user) ? 'none' : 'flex' }}
                                        >
                                          {user.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                                        </div>
                                        <span className="text-sm font-medium text-gray-900">{user.name}</span>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{user.phone || '-'}</td>
                                    <td className="px-4 py-3 text-sm">{getRoleBadge(user.role_name)}</td>
                                    <td className="px-4 py-3 text-center">
                                      <button
                                        onClick={() => {
                                          const userData = {
                                            ...user,
                                            hotel_name: filteredUsers[activeTab].hotel_name,
                                            hotel_code: filteredUsers[activeTab].hotel_code,
                                            dept_name: dept.dept_name,
                                            dept_code: dept.dept_code
                                          };
                                          setSelectedUser(userData);
                                          
                                          // Initialize form with user's current data
                                          let hotelIds = [];
                                          
                                          if (userData.arrHotel) {
                                            try {
                                              hotelIds = JSON.parse(userData.arrHotel);
                                            } catch (e) {

                                            }
                                          }
                                          
                                          // If arrHotel failed or is empty, try hotel_id
                                          if (hotelIds.length === 0 && userData.hotel_id) {
                                            hotelIds = userData.hotel_id.split(',').filter(id => id.trim());
                                          }
                                          
                                          // Convert all IDs to strings and trim whitespace
                                          const cleanedHotelIds = hotelIds.map(id => id.toString().trim());
                                          
                                          setFormData({
                                            name: userData.name,
                                            email: userData.email,
                                            phone: userData.phone || '',
                                            password: '',
                                            passwordConfirmation: '',
                                            selectedHotels: cleanedHotelIds,
                                            dept_id: userData.dept_id
                                          });
                                          setEditMode(false);
                                          setShowModal(true);
                                        }}
                                        className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                                        title="View Details"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        View
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
        )}
      </div>

      {/* User Details Modal */}
      {showModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getAvatarUrl(selectedUser.img_user) ? (
                    <img 
                      src={getAvatarUrl(selectedUser.img_user)} 
                      alt={selectedUser.name}
                      className="w-16 h-16 rounded-full object-cover border-4 border-white border-opacity-30 bg-white bg-opacity-20"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center border-4 border-white border-opacity-30"
                    style={{ display: getAvatarUrl(selectedUser.img_user) ? 'none' : 'flex' }}
                  >
                    <span className="text-2xl font-bold text-white">
                      {selectedUser.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">{selectedUser.name}</h2>
                    <p className="text-purple-200 text-sm">{selectedUser.role_name}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Contact Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Contact Information
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Email</p>
                        <p className="text-sm text-gray-900">{selectedUser.email}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Phone</p>
                        <p className="text-sm text-gray-900">{selectedUser.phone || 'Not provided'}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Role</p>
                        <div className="mt-1">{getRoleBadge(selectedUser.role_name)}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Organization Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Organization
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-lg">🏨</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 font-medium">Assigned Hotels</p>
                        {(() => {
                          let hotelIds = [];
                          try {
                            if (selectedUser.arrHotel) {
                              hotelIds = JSON.parse(selectedUser.arrHotel);
                            } else if (selectedUser.hotel_id) {
                              hotelIds = selectedUser.hotel_id.split(',');
                            }
                          } catch (e) {

                          }
                          
                          return hotelIds.length > 0 ? (
                            <div className="mt-1">
                              <p className="text-sm font-semibold text-gray-900 mb-1">
                                {hotelIds.length} hotel{hotelIds.length > 1 ? 's' : ''}
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {hotelIds.slice(0, 3).map((id, idx) => (
                                  <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                                    ID: {id}
                                  </span>
                                ))}
                                {hotelIds.length > 3 && (
                                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                                    +{hotelIds.length - 3} more
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-600">No hotels assigned</p>
                          );
                        })()}
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-lg">🏢</span>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Department</p>
                        <p className="text-sm font-semibold text-gray-900">{selectedUser.dept_name}</p>
                        <p className="text-xs text-gray-500">Code: {selectedUser.dept_code}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Member Since</p>
                        <p className="text-sm text-gray-900">{new Date(selectedUser.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* User ID */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>User ID: <span className="font-mono font-semibold text-gray-700">#{selectedUser.id}</span></span>
                  <span>Created: {new Date(selectedUser.created_at).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Edit Form (shown only in edit mode) */}
            {editMode && (
              <div className="px-6 pb-6">
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">✏️ Edit User Information</h3>
                  
                  <div className="space-y-4">
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={formData.name || selectedUser.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Current: {selectedUser.name}
                      </p>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={formData.email || selectedUser.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Current: {selectedUser.email}
                      </p>
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                      <input
                        type="tel"
                        value={formData.phone || selectedUser.phone || ''}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="e.g., 081234567890"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Current: {selectedUser.phone || 'Not set'}
                      </p>
                    </div>

                    {/* Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        New Password <span className="text-gray-400 text-xs">(leave blank to keep current)</span>
                      </label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value, passwordConfirmation: ''})}
                        placeholder="Enter new password"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                      />
                    </div>

                    {/* Password Confirmation - Only show if password is entered */}
                    {formData.password && formData.password.trim() !== '' && (
                      <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                        <label className="block text-sm font-medium text-yellow-800 mb-1">
                          🔐 Password Change Confirmation Required
                        </label>
                        <p className="text-xs text-yellow-700 mb-2">
                          To confirm password change, please enter the confirmation code
                        </p>
                        <input
                          type="text"
                          value={formData.passwordConfirmation}
                          onChange={(e) => setFormData({...formData, passwordConfirmation: e.target.value})}
                          placeholder="Enter confirmation code"
                          className="w-full px-4 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none bg-white"
                        />
                        {formData.passwordConfirmation && formData.passwordConfirmation !== 'dianfriansyah' && (
                          <p className="text-xs text-red-600 mt-1">
                            ❌ Invalid confirmation code
                          </p>
                        )}
                        {formData.passwordConfirmation === 'dianfriansyah' && (
                          <p className="text-xs text-green-600 mt-1">
                            ✅ Confirmation code verified
                          </p>
                        )}
                      </div>
                    )}

                    {/* Department */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                      <select
                        value={formData.dept_id || selectedUser.dept_id}
                        onChange={(e) => setFormData({...formData, dept_id: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                      >
                        {allDepartments.map(dept => (
                          <option key={dept.id_department} value={dept.id_department}>
                            {dept.nm_department} ({dept.code_department})
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Current: {selectedUser.dept_name} (ID: {selectedUser.dept_id})
                      </p>
                    </div>

                    {/* Hotels (Multi-select) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assign Hotels (Select multiple)
                      </label>
                      <div className="border border-gray-300 rounded-lg p-4 max-h-64 overflow-y-auto bg-gray-50">
                        {allHotels.length === 0 ? (
                          <p className="text-gray-500 text-sm text-center py-4">Loading hotels...</p>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            {allHotels.map(hotel => {
                              const hotelIdStr = hotel.id_hotel.toString();
                              // Check both as string and number to handle any format
                              const isSelected = formData.selectedHotels.some(id => 
                                id.toString() === hotelIdStr || id === hotel.id_hotel
                              );
                              
                              return (
                                <label 
                                  key={hotel.id_hotel} 
                                  className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                                    isSelected ? 'bg-purple-100 hover:bg-purple-200' : 'bg-white hover:bg-gray-100'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleHotelSelection(hotel.id_hotel)}
                                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                                  />
                                  <span className={`text-sm ${isSelected ? 'font-semibold text-purple-900' : 'text-gray-700'}`}>
                                    {hotel.nm_hotel}
                                  </span>
                                  {isSelected && <span className="ml-auto text-purple-600">✓</span>}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div className="mt-2 p-2 bg-purple-50 rounded border border-purple-200">
                        <p className="text-xs font-semibold text-purple-900 mb-1">
                          Selected: {formData.selectedHotels.length} of {allHotels.length} hotels
                        </p>
                        {formData.selectedHotels.length > 0 && (
                          <p className="text-xs text-gray-600">
                            IDs: {formData.selectedHotels.sort((a, b) => parseInt(a) - parseInt(b)).join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-between items-center gap-3">
              <div>
                {!editMode && (
                  <button
                    onClick={handleDeleteUser}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                  >
                    🗑️ Delete User
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                {editMode ? (
                  <>
                    <button
                      onClick={() => setEditMode(false)}
                      className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdateUser}
                      className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                    >
                      💾 Save Changes
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setShowModal(false)}
                      className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                    >
                      Close
                    </button>
                    <button
                      onClick={() => {
                        // Re-initialize form data with current user data when entering edit mode
                        let hotelIds = [];
                        if (selectedUser.arrHotel) {
                          try {
                            hotelIds = JSON.parse(selectedUser.arrHotel);
                          } catch (e) {

                          }
                        } else if (selectedUser.hotel_id) {
                          hotelIds = selectedUser.hotel_id.split(',').filter(id => id);
                        }
                        
                        const updatedFormData = {
                          name: selectedUser.name,
                          email: selectedUser.email,
                          phone: selectedUser.phone || '',
                          password: '',
                          passwordConfirmation: '',
                          selectedHotels: hotelIds.map(id => id.toString().trim()),
                          dept_id: selectedUser.dept_id
                        };
                        
                        setFormData(updatedFormData);
                        setEditMode(true);
                      }}
                      className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                    >
                      ✏️ Edit User
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default UsersListGrouped;
